'use client';

import { getColorScheme } from '@/utils/getColorScheme';
import { buttonLinkTargetProps } from '@/utils/buttonLinkTarget';
import { useEffect, useState } from 'react';

const ACTIONS = {
    book: {
        icon: 'fa-solid fa-calendar-check',
        label: 'Book Now',
    },
    call: {
        icon: 'fa-solid fa-phone',
        label: 'Call Now',
    },
    popup: {
        icon: 'fa-solid fa-tag',
        label: 'View Offer',
    },
    text: {
        icon: 'fa-solid fa-message-sms',
        label: 'Text Now',
    },
    whatsapp: {
        icon: 'fa-brands fa-whatsapp',
        label: 'WhatsApp',
    },
    messenger: {
        icon: 'fa-brands fa-facebook-messenger',
        label: 'Message Us',
    },
    email: {
        icon: 'fa-solid fa-envelope',
        label: 'Email Us',
    },
    directions: {
        icon: 'fa-solid fa-location-dot',
        label: 'Get Directions',
    },
};

const ICON_STYLE_TOKENS = new Set([
    'fa-solid',
    'fa-regular',
    'fa-light',
    'fa-thin',
]);
const ICON_PACK_TOKENS = new Set([
    'fa-classic',
    'fa-duotone',
    'fa-sharp',
    'fa-sharp-duotone',
]);
const ICON_PACK_CLASSES = {
    classic: [],
    duotone: ['fa-duotone'],
    sharp: ['fa-sharp'],
    'sharp-duotone': ['fa-sharp-duotone', 'fa-sharp', 'fa-duotone'],
};

function valueOf(value, fallback = '') {
    if (value && typeof value === 'object' && 'value' in value) {
        return value.value ?? fallback;
    }
    return value ?? fallback;
}

function applyIconOverride(iconValue, packValue, styleValue) {
    const tokens = String(iconValue || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (!tokens.length || tokens.includes('fa-brands')) return tokens.join(' ');

    const pack = Object.hasOwn(ICON_PACK_CLASSES, packValue)
        ? packValue
        : 'classic';
    const style = ['solid', 'regular', 'light', 'thin'].includes(styleValue)
        ? styleValue
        : 'solid';
    const cleaned = tokens.filter(
        (token) => !ICON_STYLE_TOKENS.has(token) && !ICON_PACK_TOKENS.has(token)
    );

    return [...cleaned, ...ICON_PACK_CLASSES[pack], `fa-${style}`]
        .filter((token, index, all) => all.indexOf(token) === index)
        .join(' ');
}

function useSiteIconOverride(packValue, styleValue) {
    const fallbackPack = String(valueOf(packValue, 'classic'));
    const fallbackStyle = String(valueOf(styleValue, 'solid'));
    const [override, setOverride] = useState({
        pack: fallbackPack,
        style: fallbackStyle,
    });

    useEffect(() => {
        if (typeof document === 'undefined') return undefined;

        const root = document.documentElement;
        const update = () => {
            const pack = root.dataset.faPackOverride || fallbackPack;
            const style = root.dataset.faStyleOverride || fallbackStyle;
            setOverride((previous) =>
                previous.pack === pack && previous.style === style
                    ? previous
                    : { pack, style }
            );
        };

        update();
        const observer = new MutationObserver(update);
        observer.observe(root, {
            attributes: true,
            attributeFilter: [
                'data-fa-pack-override',
                'data-fa-style-override',
            ],
        });
        return () => observer.disconnect();
    }, [fallbackPack, fallbackStyle]);

    return override;
}

function coerceBoolean(value, fallback = true) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['false', '0', 'no', 'off'].includes(normalized)) return false;
        if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    }
    return fallback;
}

function normalizeAction(value) {
    const normalized = String(value || 'text')
        .trim()
        .toLowerCase();
    return ACTIONS[normalized] ? normalized : 'text';
}

function normalizePhone(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const digits = raw.replace(/\D/g, '');
    if (!digits) return '';
    const prefix = raw.startsWith('+') ? '+' : '';
    return `${prefix}${digits}`;
}

function normalizeBookingLink(value) {
    const raw = String(value || '').trim();
    if (!raw) return '#';
    if (/^(https?:|mailto:|tel:|sms:|\/|#)/i.test(raw)) return raw;
    return `https://${raw}`;
}

function normalizeMessengerLink(value) {
    const raw = String(value || '').trim();
    if (!raw) return '#';
    if (/^https?:\/\//i.test(raw)) return raw;
    if (/^(?:www\.)?(?:m\.me|facebook\.com)\//i.test(raw)) {
        return `https://${raw}`;
    }
    return `https://m.me/${raw.replace(/^@/, '')}`;
}

function getEmailHref(address, subject, message) {
    const email = String(address || '')
        .trim()
        .replace(/^mailto:/i, '');
    if (!email) return '#';

    const params = new URLSearchParams();
    const normalizedSubject = String(subject || '').trim();
    const normalizedMessage = String(message || '').trim();
    if (normalizedSubject) params.set('subject', normalizedSubject);
    if (normalizedMessage) params.set('body', normalizedMessage);

    const query = params.toString();
    return `mailto:${email}${query ? `?${query}` : ''}`;
}

function getDirectionsHref(value) {
    const destination = String(value || '').trim();
    if (!destination) return '#';
    if (/^https?:\/\//i.test(destination)) return destination;
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        destination
    )}`;
}

function getActionHref(
    action,
    phoneNumber,
    bookingLink,
    messengerLink,
    textMessage,
    emailAddress,
    emailSubject,
    emailMessage,
    directionsDestination
) {
    if (action === 'book') return normalizeBookingLink(bookingLink);
    if (action === 'popup') return '#';
    if (action === 'messenger') return normalizeMessengerLink(messengerLink);
    if (action === 'email') {
        return getEmailHref(emailAddress, emailSubject, emailMessage);
    }
    if (action === 'directions') {
        return getDirectionsHref(directionsDestination);
    }

    const phone = normalizePhone(phoneNumber);
    if (!phone) return '#';
    if (action === 'call') return `tel:${phone}`;

    const message = String(textMessage || '').trim();
    if (action === 'whatsapp') {
        const whatsappPhone = phone.replace(/\D/g, '');
        return `https://wa.me/${whatsappPhone}${
            message ? `?text=${encodeURIComponent(message)}` : ''
        }`;
    }
    return `sms:${phone}${
        message ? `?body=${encodeURIComponent(message)}` : ''
    }`;
}

export default function FloatingAction(props) {
    const {
        Enabled = true,
        Type,
        Action,
        Label,
        PhoneNumber = '',
        BookingLink = '',
        BookingLinkOpenInNewTab = false,
        MessengerLink = '',
        TextMessage = '',
        EmailAddress = '',
        EmailSubject = '',
        EmailMessage = '',
        DirectionsDestination = '',
        DirectionsOpenInNewTab = true,
        Icon = '',
        IconPackOverride = 'classic',
        IconStyleOverride = 'solid',
        ShowLabel = true,
        Position = 'right',
        ColorScheme = 'primary',
        GradientDirection = 180,
        is_edit_mode = false,
    } = props;

    const siteIconOverride = useSiteIconOverride(
        IconPackOverride,
        IconStyleOverride
    );
    const enabled = coerceBoolean(Enabled, true);
    if (!enabled) return null;

    const action = normalizeAction(valueOf(Type) || valueOf(Action));
    const actionConfig = ACTIONS[action];
    const label = String(Label || '').trim() || actionConfig.label;
    const selectedIcon =
        String(valueOf(Icon) || '').trim() || actionConfig.icon;
    const icon = applyIconOverride(
        selectedIcon,
        siteIconOverride.pack,
        siteIconOverride.style
    );
    const showLabel = coerceBoolean(ShowLabel, true);
    const isLeft = String(Position || '').toLowerCase() === 'left';
    const hasBookingLink = Boolean(String(BookingLink || '').trim());
    const useNiceJobLead = action === 'book' && !hasBookingLink;
    const usePopupAction = action === 'popup';
    const href = getActionHref(
        action,
        PhoneNumber,
        BookingLink,
        MessengerLink,
        TextMessage,
        EmailAddress,
        EmailSubject,
        EmailMessage,
        DirectionsDestination
    );
    const {
        backgroundColor,
        boxColor,
        boxBorder,
        textColor,
        boxTextColor,
        backgroundStyle,
    } = getColorScheme(ColorScheme, GradientDirection);
    const surfaceColor = boxColor || backgroundColor;
    const surfaceTextColor = boxTextColor || textColor;
    const isUnavailable = href === '#' && !useNiceJobLead && !usePopupAction;
    const actionClassName = `group inline-flex min-h-14 items-center justify-center gap-2 rounded-[var(--shape-radius)] border border-white/20 px-4 text-sm font-bold shadow-xl ring-1 ring-black/10 transition duration-200 hover:-translate-y-0.5 hover:shadow-2xl focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-300 ${surfaceColor} ${surfaceTextColor} ${
        boxBorder || ''
    } ${isUnavailable ? 'cursor-not-allowed' : ''} ${
        showLabel ? '' : 'h-14 w-14 px-0'
    }`;

    const actionContent = (
        <>
            <i className={`${icon} text-xl`} aria-hidden="true" />
            {showLabel && <span className="whitespace-nowrap">{label}</span>}
        </>
    );

    const handleClick = (event) => {
        if (is_edit_mode) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        if (isUnavailable) event.preventDefault();
    };

    const handlePopupClick = (event) => {
        handleClick(event);
        if (typeof window === 'undefined') return;
        window.dispatchEvent(new CustomEvent('site:open-popup'));
    };

    return (
        <div
            data-component="FloatingAction"
            className={`fixed bottom-5 z-[900] sm:bottom-7 ${
                isLeft ? 'left-5 sm:left-7' : 'right-5 sm:right-7'
            }`}
        >
            {useNiceJobLead ? (
                <button
                    type="button"
                    onClick={handleClick}
                    aria-label={label}
                    title={label}
                    className={`${actionClassName} nj-lead`}
                    style={backgroundStyle}
                >
                    {actionContent}
                </button>
            ) : usePopupAction ? (
                <button
                    type="button"
                    onClick={handlePopupClick}
                    aria-label={label}
                    title={label}
                    className={actionClassName}
                    style={backgroundStyle}
                >
                    {actionContent}
                </button>
            ) : (
                <a
                    href={href}
                    onClick={handleClick}
                    aria-label={label}
                    aria-disabled={isUnavailable}
                    title={label}
                    className={actionClassName}
                    style={backgroundStyle}
                    {...(!is_edit_mode && action === 'book'
                        ? buttonLinkTargetProps(BookingLinkOpenInNewTab)
                        : !is_edit_mode && action === 'directions'
                        ? buttonLinkTargetProps(DirectionsOpenInNewTab)
                        : {})}
                >
                    {actionContent}
                </a>
            )}
        </div>
    );
}
